import type { Membership, Role } from '@sazeo/schemas';
import { getTenantClient } from '@sazeo/supabase-tenant';
import { type CoreError, coreError } from './errors';

/**
 * Check whether the given user is a member of the given tenant.
 * Used by the tenant-app root loader to gate subdomain access.
 */
export const isTenantMember = async (
  userId: string,
  tenantId: string,
): Promise<boolean | CoreError> => {
  const client = getTenantClient();
  const { data, error } = await client
    .from('memberships')
    .select('id')
    .eq('user_id', userId)
    .eq('tenant_id', tenantId)
    .maybeSingle();

  if (error) return coreError('INTERNAL', error.message);
  return data !== null;
};

/**
 * List the current user's memberships for a tenant, with resolved role.
 */
export const getMembership = async (
  userId: string,
  tenantId: string,
): Promise<(Membership & { role: Role | null }) | CoreError> => {
  const client = getTenantClient();
  const { data, error } = await client
    .from('memberships')
    .select(
      'id, user_id, tenant_id, role_id, joined_at, role:roles(id, tenant_id, name, scopes, is_system, created_at)',
    )
    .eq('user_id', userId)
    .eq('tenant_id', tenantId)
    .maybeSingle();

  if (error) return coreError('INTERNAL', error.message);
  if (!data) return coreError('NOT_FOUND', 'Not a member of this tenant');
  return data as unknown as Membership & { role: Role | null };
};

/**
 * Add a member to a tenant. Admin-only at policy layer.
 */
export const addMember = async (args: {
  userId: string;
  tenantId: string;
  roleName: 'owner' | 'admin' | 'member';
}): Promise<Membership | CoreError> => {
  const client = getTenantClient();

  const roleRes = await client
    .from('roles')
    .select('id')
    .eq('is_system', true)
    .eq('name', args.roleName)
    .maybeSingle();
  if (roleRes.error || !roleRes.data) {
    return coreError('INTERNAL', `System role "${args.roleName}" missing`);
  }

  const insert = await client
    .from('memberships')
    .insert({
      user_id: args.userId,
      tenant_id: args.tenantId,
      role_id: roleRes.data.id,
    })
    .select()
    .single();

  if (insert.error) return coreError('INTERNAL', insert.error.message);
  return insert.data as Membership;
};
